<script setup>
import { reactive, computed, watch, getCurrentInstance } from 'vue';
import { Countries } from '/src/app/features/shared/libraries/countries.js';

const { proxy } = getCurrentInstance();
const { $bus, $constants, $stores } = proxy;

const sourceData = computed(() => $stores.appData?.state?.data?.rates || {});

// Get country codes from source data keys
const countryCodes = computed(() =>
  Object.keys(sourceData.value).filter((code) => sourceData.value[code])
);

const form = reactive({});

// Initialize form for each country
watch(
  sourceData,
  (data) => {
    if (!data) return;
    Object.keys(data).forEach((code) => {
      if (data[code] && !form[code]) {
        const country = Countries.find(code);
        const currency = country?.currency || 'XOF';
        form[code] = {
          buyRate: 0,
          sellRate: 0,
          currency
        };
      }
      if (data[code] && form[code]) {
        const country = Countries.find(code);
        const currency = country?.currency || 'XOF';
        form[code].buyRate = data[code].buyRates?.USDT?.[currency] || 0;
        form[code].sellRate = data[code].sellRates?.USDT?.[currency] || 0;
        form[code].currency = currency;
      }
    });
  },
  { immediate: true, deep: true }
);

const hasChanges = computed(() => {
  return countryCodes.value.some((code) => {
    const currency = form[code]?.currency || 'XOF';
    return (
      form[code]?.buyRate !==
        sourceData.value?.[code]?.buyRates?.USDT?.[currency] ||
      form[code]?.sellRate !==
        sourceData.value?.[code]?.sellRates?.USDT?.[currency]
    );
  });
});

const countryHasChanges = (code) => {
  const currency = form[code]?.currency || 'XOF';
  return (
    form[code]?.buyRate !==
      sourceData.value?.[code]?.buyRates?.USDT?.[currency] ||
    form[code]?.sellRate !==
      sourceData.value?.[code]?.sellRates?.USDT?.[currency]
  );
};

function save() {
  if (!hasChanges.value) return;

  const payload = { rates: {} };

  countryCodes.value.forEach((code) => {
    const currency = form[code]?.currency || 'XOF';
    payload.rates[code] = {};
    if (
      form[code].buyRate !==
      sourceData.value?.[code]?.buyRates?.USDT?.[currency]
    ) {
      payload.rates[code].buyRates = {
        USDT: { [currency]: form[code].buyRate }
      };
    }
    if (
      form[code].sellRate !==
      sourceData.value?.[code]?.sellRates?.USDT?.[currency]
    ) {
      payload.rates[code].sellRates = payload.rates[code].sellRates || {};
      payload.rates[code].sellRates = {
        USDT: { [currency]: form[code].sellRate }
      };
    }
  });

  $bus.emit($constants.EVENTS.APP_DATA.UPDATE_RATES, payload);
}

function reset() {
  countryCodes.value.forEach((code) => {
    const currency = form[code]?.currency || 'XOF';
    if (sourceData.value?.[code]) {
      form[code].buyRate =
        sourceData.value[code].buyRates?.USDT?.[currency] || 0;
      form[code].sellRate =
        sourceData.value[code].sellRates?.USDT?.[currency] || 0;
    }
  });
}

function resetCountry(code) {
  const currency = form[code]?.currency || 'XOF';
  if (sourceData.value?.[code]) {
    form[code].buyRate = sourceData.value[code].buyRates?.USDT?.[currency] || 0;
    form[code].sellRate =
      sourceData.value[code].sellRates?.USDT?.[currency] || 0;
  }
}
</script>

<template>
  <div class="space-y-6">
    <!-- HEADER -->
    <div class="flex justify-between items-center">
      <h2 class="text-xl font-semibold">Taux de change USDT</h2>
      <div class="flex gap-3" v-show="hasChanges">
        <button
          class="px-6 py-2 rounded-xl bg-gray-300 text-gray-700"
          @click="reset"
        >
          Annuler tout
        </button>
        <button
          class="px-6 py-2 rounded-xl bg-primary-600 text-white"
          @click="save"
        >
          Sauvegarder
        </button>
      </div>
    </div>

    <!-- COUNTRIES GRID -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <div
        v-for="code in countryCodes"
        :key="code"
        class="bg-slate-100 rounded-2xl p-5 shadow"
      >
        <div
          class="flex justify-between items-center mb-4 pb-3 border-b border-gray-300"
        >
          <div>
            <h3 class="font-semibold">
              {{ Countries.find(code)?.countryName || code }}
            </h3>
            <span class="text-sm text-gray-500">{{ code }}</span>
          </div>
          <button
            v-if="countryHasChanges(code)"
            class="text-xs px-3 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
            @click="resetCountry(code)"
          >
            Annuler
          </button>
        </div>

        <div class="space-y-3">
          <div>
            <label class="block mb-1 text-sm font-medium"
              >Achat USDT/{{ form[code]?.currency }}</label
            >
            <input
              type="number"
              class="inputComponent"
              v-model.number="form[code].buyRate"
            />
          </div>

          <div>
            <label class="block mb-1 text-sm font-medium"
              >Vente USDT/{{ form[code]?.currency }}</label
            >
            <input
              type="number"
              class="inputComponent"
              v-model.number="form[code].sellRate"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
